import { randomBytes, randomUUID, timingSafeEqual } from "node:crypto";
import { createServer } from "node:http";
import { Server } from "@modelcontextprotocol/sdk/server/index.js";
import { StreamableHTTPServerTransport } from "@modelcontextprotocol/sdk/server/streamableHttp.js";
import { CallToolRequestSchema, ListToolsRequestSchema } from "@modelcontextprotocol/sdk/types.js";
import { dispatchCapability } from "../../bridge/src/capability-dispatch.js";
import type { CodexServerRequestHandlers } from "../../bridge/src/codex-app-server.js";
import type { BridgeCapabilityHooks, CodexToolTransport } from "../../bridge/src/types.js";

/** Trusted runner decisions; no native session-wide grant or policy amendment is inferred. */
export type CodexNativeApprovalHandlers = Pick<
  CodexServerRequestHandlers,
  | "item/commandExecution/requestApproval"
  | "item/fileChange/requestApproval"
  | "item/permissions/requestApproval"
>;

async function cancellable<T>(work: Promise<T>, signal: AbortSignal): Promise<T> {
  let abort = () => {};
  try {
    return await Promise.race([
      work,
      new Promise<never>((_resolve, reject) => {
        abort = () => reject(new Error("CODEX_TOOL_CANCELLED"));
        signal.addEventListener("abort", abort, { once: true });
        if (signal.aborted) abort();
      }),
    ]);
  } finally {
    signal.removeEventListener("abort", abort);
  }
}

/** One private MCP listener over the existing live capability dispatcher. */
export async function prepareCodexTools(options: {
  hooks: BridgeCapabilityHooks;
  requiredTools: readonly string[];
  signal: AbortSignal;
  approvals: CodexNativeApprovalHandlers;
}): Promise<CodexToolTransport> {
  options.signal.throwIfAborted();
  if (!options.requiredTools.length) throw new Error("CODEX_REQUIRED_TOOL_UNAVAILABLE");
  const { hooks } = options;
  let turnSignal: AbortSignal | undefined;
  let toolsListTotal = 0;
  let toolsCallTotal = 0;
  const lifetime = new AbortController();
  const signal = AbortSignal.any([options.signal, lifetime.signal]);
  const list = () =>
    hooks.composeTools().filter((tool) => {
      const cap = hooks.resolve(tool.name);
      return (
        cap &&
        (cap.audience ?? ["llm", "user"]).includes("llm") &&
        !tool.name.startsWith("host.") &&
        !tool.name.startsWith("runner.")
      );
    });
  const validate = async () => {
    signal.throwIfAborted();
    const names = new Set(list().map((tool) => tool.name));
    if (options.requiredTools.some((name) => !names.has(name)))
      throw new Error("CODEX_REQUIRED_TOOL_UNAVAILABLE");
  };
  await validate();
  const signature = () => JSON.stringify(list());
  const initialSignature = signature();
  const bearer = randomBytes(32).toString("base64url");
  const authorization = Buffer.from(`Bearer ${bearer}`);
  const peers = new Set<Server>();
  const transports = new Map<string, StreamableHTTPServerTransport>();
  let closed = false;
  const http = createServer((request, response) => {
    void (async () => {
      const supplied = Buffer.from(request.headers.authorization ?? "");
      if (
        closed ||
        supplied.length !== authorization.length ||
        !timingSafeEqual(supplied, authorization)
      ) {
        response.writeHead(401).end();
        return;
      }
      if (request.url !== "/mcp") {
        response.writeHead(404).end();
        return;
      }
      const id = request.headers["mcp-session-id"];
      let transport = typeof id === "string" ? transports.get(id) : undefined;
      let allocation: Server | undefined;
      try {
        if (!transport) {
          if (id || request.method !== "POST") {
            response.writeHead(400).end();
            return;
          }
          if (peers.size >= 2) {
            response.writeHead(429).end();
            return;
          }
          const server = new Server(
            { name: "skaile-capabilities", version: "1" },
            { capabilities: { tools: { listChanged: true } } },
          );
          transport = new StreamableHTTPServerTransport({
            sessionIdGenerator: randomUUID,
            onsessioninitialized: (sessionId) => {
              transports.set(sessionId, transport!);
            },
          });
          peers.add(server);
          allocation = server;
          server.setRequestHandler(ListToolsRequestSchema, async () => {
            toolsListTotal = Math.min(512, toolsListTotal + 1);
            return {
              tools: list().map((tool) => ({
                name: tool.name,
                description: tool.description,
                inputSchema: { ...tool.parameters, type: "object" as const },
              })),
            };
          });
          server.setRequestHandler(CallToolRequestSchema, async (call, extra) => {
            toolsCallTotal = Math.min(512, toolsCallTotal + 1);
            const currentTurn = turnSignal;
            const cancellation = AbortSignal.any([
              signal,
              extra.signal,
              ...(currentTurn ? [currentTurn] : []),
            ]);
            try {
              cancellation.throwIfAborted();
              if (!currentTurn || currentTurn !== turnSignal)
                throw new Error("CODEX_TOOL_UNAVAILABLE");
              if (!list().some((tool) => tool.name === call.params.name))
                throw new Error("CODEX_TOOL_UNAVAILABLE");
              const { result } = await cancellable(
                dispatchCapability(
                  hooks,
                  randomUUID(),
                  call.params.name,
                  call.params.arguments ?? {},
                  () => {},
                  cancellation,
                ),
                cancellation,
              );
              cancellation.throwIfAborted();
              if (!currentTurn || currentTurn !== turnSignal)
                throw new Error("CODEX_TOOL_UNAVAILABLE");
              const payload = result === undefined ? {} : result;
              const isError =
                typeof payload === "object" &&
                payload !== null &&
                (("error" in payload && typeof payload.error === "string") ||
                  ("ok" in payload && payload.ok === false) ||
                  ("isError" in payload && payload.isError === true));
              if (
                typeof payload === "object" &&
                payload !== null &&
                "content" in payload &&
                Array.isArray(payload.content)
              )
                return { content: payload.content, isError };
              return {
                content: [{ type: "text" as const, text: JSON.stringify(payload) }],
                isError,
              };
            } catch {
              return {
                content: [
                  {
                    type: "text" as const,
                    text: cancellation.aborted ? "CODEX_TOOL_CANCELLED" : "CODEX_TOOL_FAILED",
                  },
                ],
                isError: true,
              };
            }
          });
          server.onclose = () => {
            peers.delete(server);
            if (transport?.sessionId) transports.delete(transport.sessionId);
          };
          await server.connect(transport);
        }
        await transport.handleRequest(request, response);
      } finally {
        // Invalid initialization returns HTTP errors without closing the SDK server.
        // Only initialized sessions may retain a bounded reconnect slot.
        if (allocation && !transport?.sessionId) {
          peers.delete(allocation);
          await allocation.close();
        }
      }
    })().catch(() => {
      if (!response.headersSent) response.writeHead(500).end();
      else response.destroy();
    });
  });
  const unsubscribe = hooks.onToolsChanged?.(() => {
    for (const peer of peers) void peer.sendToolListChanged().catch(() => {});
  });
  let disposal: Promise<void> | undefined;
  const dispose = (): Promise<void> =>
    (disposal ??= (async () => {
      closed = true;
      lifetime.abort();
      unsubscribe?.();
      options.signal.removeEventListener("abort", abort);
      await Promise.allSettled([...peers].map((peer) => peer.close()));
      http.closeAllConnections();
      if (http.listening) await new Promise<void>((resolve) => http.close(() => resolve()));
    })());
  const abort = () => {
    void dispose();
  };
  options.signal.addEventListener("abort", abort, { once: true });
  try {
    signal.throwIfAborted();
    await new Promise<void>((resolve, reject) => {
      http.once("error", reject);
      http.listen(0, "127.0.0.1", resolve);
    });
    signal.throwIfAborted();
    const address = http.address();
    if (!address || typeof address === "string") throw new Error("CODEX_REQUIRED_TOOL_UNAVAILABLE");
    return {
      approvalServerName: "skaile-capabilities",
      diagnostics: {
        serverName: "skaile-capabilities",
        requestCounts: () => ({ toolsListTotal, toolsCallTotal }),
      },
      mcpServers: {
        "skaile-capabilities": {
          url: `http://127.0.0.1:${address.port}/mcp`,
          http_headers: { Authorization: `Bearer ${bearer}` },
          required: true,
        },
      },
      onServerRequest: Object.fromEntries(
        Object.entries(options.approvals).filter(
          ([method, handler]) =>
            [
              "item/commandExecution/requestApproval",
              "item/fileChange/requestApproval",
              "item/permissions/requestApproval",
            ].includes(method) && typeof handler === "function",
        ),
      ) as CodexNativeApprovalHandlers,
      beginTurn: (next) => {
        turnSignal = next;
      },
      validate,
      needsReload: () => signature() !== initialSignature,
      instructions: `Available Skaile MCP tools: ${list()
        .map((tool) => tool.name)
        .join(", ")}`,
      dispose,
    };
  } catch {
    await dispose();
    http.closeAllConnections();
    if (http.listening) await new Promise<void>((resolve) => http.close(() => resolve()));
    throw new Error("CODEX_REQUIRED_TOOL_UNAVAILABLE");
  }
}
